import { httpService } from './httpService';
import { API_BASE_URL, REQ_ENC_KEY } from './api';
import { decryptResponse, encryptResponse } from './encrypt';
import { getSessionKey } from './sessionKey';
import {
  CallFeatureFlags,
  ChatFeatureFlags,
  BotFeatureFlags,
  DEFAULT_BOT_FEATURES,
  DEFAULT_CALL_FEATURES,
  DEFAULT_CHAT_FEATURES,
  DEFAULT_TABS,
  DEFAULT_WIDGET_COLORS,
  Tab,
  WidgetBuilderConfig,
  WidgetColors,
  WidgetThemeMode,
  WidgetTypography,
} from '../types/widgetBuilder';

export type CustomKitVariant = {
  _id?: string;
  organization_id?: string;
  application_id?: string;
  varient_id: string;
  is_bot_varient: boolean;
  platform_type: string;
  platform_name: string;
  theme?: WidgetThemeMode | string;
  colors?: Partial<WidgetColors>;
  typography?: Partial<WidgetTypography>;
  chat_feature?: Partial<ChatFeatureFlags>;
  call_feature?: Partial<CallFeatureFlags>;
  bot_features?: Partial<BotFeatureFlags>;
  with_sidebar?: boolean;
  tabs?: Partial<Tab>;
  /** @deprecated legacy key — mapped into tabs during normalize */
  layout_tabs?: Partial<Record<string, boolean>>;
  createdAt?: string;
  updatedAt?: string;
};

export type AddCustomKitVariantPayload = {
  organization_id: string;
  application_id: string;
  is_bot_varient: boolean;
  platform_type: string;
  platform_name: string;
};

const extractVariants = (decrypted: unknown): CustomKitVariant[] => {
  if (Array.isArray(decrypted)) return decrypted as CustomKitVariant[];
  if (decrypted && typeof decrypted === 'object') {
    const data = decrypted as Record<string, unknown>;
    if (Array.isArray(data.varientDetails)) return data.varientDetails as CustomKitVariant[];
    if (Array.isArray(data.varients)) return data.varients as CustomKitVariant[];
    if (Array.isArray(data.variants)) return data.variants as CustomKitVariant[];
    if (Array.isArray(data.data)) return data.data as CustomKitVariant[];
  }
  return [];
};

const mapLegacyTabs = (layoutTabs?: Partial<Record<string, boolean>>): Partial<Tab> => {
  if (!layoutTabs) return {};
  return {
    users_tab: layoutTabs.users_tab ?? layoutTabs.users,
    group_tab: layoutTabs.group_tab ?? layoutTabs.groups,
    call_history_tab: layoutTabs.call_history_tab ?? layoutTabs.call_logs,
    schedule_call_history_tab:
      layoutTabs.schedule_call_history_tab ?? layoutTabs.conversations,
  };
};

export const normalizeVariantConfig = (variant: CustomKitVariant): WidgetBuilderConfig => {
  const theme = (variant.theme as WidgetThemeMode) || 'light';
  const legacyTabs = mapLegacyTabs(variant.layout_tabs);
  return {
    varient_id: variant.varient_id,
    platform_name: variant.platform_name || 'Widget',
    platform_type: variant.platform_type || 'nocode',
    is_bot_varient: Boolean(variant.is_bot_varient),
    theme: ['system', 'light', 'dark'].includes(theme) ? theme : 'light',
    colors: { ...DEFAULT_WIDGET_COLORS, ...(variant.colors || {}) },
    typography: {
      font: variant.typography?.font || 'Poppins',
      size: (variant.typography?.size as WidgetTypography['size']) || 'default',
    },
    with_sidebar: variant.with_sidebar !== false,
    tabs: { ...DEFAULT_TABS, ...legacyTabs, ...(variant.tabs || {}) },
    chat_feature: { ...DEFAULT_CHAT_FEATURES, ...(variant.chat_feature || {}) },
    call_feature: { ...DEFAULT_CALL_FEATURES, ...(variant.call_feature || {}) },
    bot_features: { ...DEFAULT_BOT_FEATURES, ...(variant.bot_features || {}) },
  };
};

const extractSingleVariant = (decrypted: unknown): CustomKitVariant | null => {
  if (!decrypted || typeof decrypted !== 'object') return null;
  const data = decrypted as Record<string, unknown>;

  if (typeof data.varient_id === 'string') {
    return decrypted as CustomKitVariant;
  }
  if (data.varientDetails && typeof data.varientDetails === 'object') {
    return data.varientDetails as CustomKitVariant;
  }
  if (data.variant && typeof data.variant === 'object') {
    return data.variant as CustomKitVariant;
  }
  if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
    return data.data as CustomKitVariant;
  }

  const list = extractVariants(decrypted);
  return list[0] || null;
};

export const getAllCustomKitVariants = async (
  organizationId: string,
  applicationId: string
): Promise<CustomKitVariant[]> => {
  const payload = {
    data: await encryptResponse(
      {
        organization_id: organizationId,
        application_id: applicationId,
      },
      REQ_ENC_KEY
    ),
  };

  const response = await httpService.post<any>(
    `${API_BASE_URL}/user/getAllCustomKitVarients`,
    payload
  );

  if (response.data?.status === 200 && response.data.data) {
    const decrypted = await decryptResponse(
      response.data.data,
      getSessionKey() as string
    );
    return extractVariants(decrypted);
  }

  return [];
};

export type GetCustomKitVariantPayload = {
  organization_id: string;
  application_id: string;
  varient_id: string;
};

export const getCustomKitVariant = async (
  input: GetCustomKitVariantPayload
): Promise<CustomKitVariant | null> => {
  const payload = {
    data: await encryptResponse(input, REQ_ENC_KEY),
  };

  const response = await httpService.post<any>(
    `${API_BASE_URL}/user/getCustomKitVarient`,
    payload
  );

  if (response.data?.status === 200 && response.data.data) {
    const decrypted = await decryptResponse(
      response.data.data,
      getSessionKey() as string
    );
    return extractSingleVariant(decrypted);
  }

  return null;
};

export type UpdateCustomKitVariantPayload = {
  organization_id: string;
  application_id: string;
  varient_id: string;
  theme: WidgetThemeMode | string;
  colors: WidgetColors;
  typography: WidgetTypography;
  bot_features?: BotFeatureFlags;
  chat_feature?: ChatFeatureFlags;
  call_feature?: CallFeatureFlags;
  tabs?: Tab;
};

export const buildUpdateCustomKitVariantPayload = (
  organizationId: string,
  applicationId: string,
  config: WidgetBuilderConfig
): UpdateCustomKitVariantPayload => {
  const base: UpdateCustomKitVariantPayload = {
    organization_id: organizationId,
    application_id: applicationId,
    varient_id: config.varient_id,
    theme: config.theme,
    colors: config.colors,
    typography: config.typography,
  };

  if (config.is_bot_varient) {
    return {
      ...base,
      bot_features: config.bot_features,
    };
  }

  return {
    ...base,
    chat_feature: config.chat_feature,
    call_feature: config.call_feature,
    tabs: config.tabs,
  };
};

export const updateCustomKitVariant = async (
  input: UpdateCustomKitVariantPayload
): Promise<CustomKitVariant | null> => {
  const payload = {
    data: await encryptResponse(input, REQ_ENC_KEY),
  };

  const response = await httpService.post<any>(
    `${API_BASE_URL}/user/updateCustomKitVarient`,
    payload
  );

  if (response.data?.status === 200 && response.data.data) {
    const decrypted = await decryptResponse(
      response.data.data,
      getSessionKey() as string
    );
    return extractSingleVariant(decrypted);
  }

  if (response.data?.status === 200) {
    return { varient_id: input.varient_id } as CustomKitVariant;
  }

  throw new Error(response.data?.message || 'Failed to update custom kit variant');
};

export const addCustomKitVariant = async (
  input: AddCustomKitVariantPayload
): Promise<CustomKitVariant | null> => {
  const payload = {
    data: await encryptResponse(input, REQ_ENC_KEY),
  };

  const response = await httpService.post<any>(
    `${API_BASE_URL}/user/addCustomKitVarient`,
    payload
  );

  if (response.data?.status === 200 && response.data.data) {
    const decrypted = await decryptResponse<CustomKitVariant>(
      response.data.data,
      getSessionKey() as string
    );
    return decrypted;
  }

  return null;
};
